import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import PatientDetails from "@components/PatientDetails/PatientDetails";
import { OrderOut } from "@providers/hop-ord-server/api";
import {
  CompletedImage,
  PendingScorecard,
  CompletedWalkthrough,
  CompletedMedia,
} from "@utils/useManageAttachments";
import { useMemo } from "react";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
  attachments: (CompletedMedia | PendingScorecard)[];
  orderData: OrderOut;
}
const SendForReviewDialog = ({
  open,
  setOpen,
  onConfirm,
  attachments,
  orderData,
}: Props) => {
  // Split the walkthrough files into video and image files
  const { walkthroughs, images, scorecards } = useMemo(() => {
    return {
      walkthroughs: attachments.filter(
        (att): att is CompletedWalkthrough => att.category === "walkthrough",
      ),
      images: attachments.filter(
        (att): att is CompletedImage => att.category === "image",
      ),
      scorecards: attachments.filter(
        (att): att is PendingScorecard => att.category === "scorecard",
      ),
    };
  }, [attachments]);

  return (
    <Dialog open={open} maxWidth="sm" data-testid="send-for-review-dialog">
      <DialogTitle>Send for review?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <div>
            Are you sure you want to send the following documents for RO
            approval? Invalid files will not be sent.
          </div>
          {scorecards.length > 0 && (
            <>
              <Typography variant="button">Scorecard</Typography>
              <ol style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {scorecards.map((att, idx) => (
                  <li key={idx} data-testid={`scorecard-${idx}`}>
                    {att.name}
                  </li>
                ))}
              </ol>
            </>
          )}
          {walkthroughs.length > 0 && (
            <>
              <Typography variant="button">Plan walkthrough</Typography>
              <ol style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {walkthroughs.map((att, idx) => (
                  <li key={idx} data-testid={`walkthrough-${idx}`}>
                    {att.name}
                  </li>
                ))}
              </ol>
            </>
          )}
          {images.length > 0 && (
            <>
              <Typography variant="button">Images</Typography>
              <ol style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {images.map((att, idx) => (
                  <li key={idx} data-testid={`image-${idx}`}>
                    {att.name}
                  </li>
                ))}
              </ol>
            </>
          )}
          <Divider />
          <PatientDetails
            name={orderData?.patientName || ""}
            dob={orderData?.patientDob || ""}
            patId={orderData?.patientMrn || ""}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-testid="dialog-cancel-btn" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button data-testid="dialog-send-btn" onClick={onConfirm}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendForReviewDialog;
